/**
 * Warranty tier finishes: the token vocabulary and its stylesheet mapping.
 *
 * The backend sends a `finish` token from `WarrantyCategory.TierFinish`; this is
 * the allowlist it maps into. A token that is not listed here degrades to the
 * neutral badge rather than rendering unstyled, so a stale or bad value can
 * never leak a tenant's palette or break a page.
 *
 * Kept out of the component file so it can be imported by non-component code
 * (and so fast refresh keeps working there).
 */

export type TierFinish =
  | "neutral"
  | "brand"
  | "bronze"
  | "silver"
  | "gold"
  | "platinum";

export const FINISH_CLASS: Record<TierFinish, string> = {
  neutral: "tier-finish-neutral",
  brand: "tier-finish-brand",
  bronze: "tier-finish-bronze",
  silver: "tier-finish-silver",
  gold: "tier-finish-gold",
  platinum: "tier-finish-platinum",
};

export function resolveFinish(finish: string | null | undefined): TierFinish {
  if (finish && finish in FINISH_CLASS) return finish as TierFinish;
  return "neutral";
}

/**
 * The stylesheet class for a finish token, for callers applying a tier's
 * treatment to something that is not a badge - a card header, say. Returns null
 * for the neutral/unknown case so the caller keeps its own default.
 */
export function tierFinishClass(finish?: string | null): string | null {
  const resolved = resolveFinish(finish);
  return resolved === "neutral" ? null : FINISH_CLASS[resolved];
}
